import axios, {
  AxiosError,
  AxiosProgressEvent,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import { jwtDecode } from "jwt-decode";

import { Authentication as AuthenticationType } from "../types/authen";
import { fetcher } from "./fetch";

interface DecodedToken {
  exp: number;
}

const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT;

const defaultHeaders = {
  "Content-Type": "application/json",
  // "Cache-Control": "no-cache",
};

const getUser = (): any | null => {
  let user = null;
  if (localStorage.getItem("user")) {
    user = JSON.parse(localStorage.getItem("user")!);
  }
  return user;
};

export const getAccessToken = (): any | null => {
  const user = getUser();
  const accessToken = user?.state?.user?.access_token
    ? user?.state?.user?.access_token
    : null;
  return accessToken;
};

const setUserWithAccessToken = (user: any) => {
  localStorage.setItem("user", JSON.stringify({ state: { user } }));
};

const isAuthFailure = (error: unknown): boolean => {
  const status = (error as AxiosError | undefined)?.response?.status;
  return status === 401 || status === 403;
};

const performRefresh = async (): Promise<string | null> => {
  try {
    const response: AuthenticationType = await fetcher(
      "POST",
      "refresh-token",
      null,
      null,
      null,
      true,
    );

    let user: AuthenticationType | null = null;
    let newAccessToken = null;
    if (response) {
      user = {
        ...response,
        expire_at:
          jwtDecode<DecodedToken>(response?.access_token).exp * 1000 - 15000,
      };
      newAccessToken = response?.access_token;
    }
    setUserWithAccessToken(user);
    return newAccessToken;
  } catch (error) {
    console.error("Failed to refresh access token:", error);
    // Only a definitive refusal (the cookie is gone, rotated or revoked) ends the
    // session. A network blip or a 5xx keeps the user signed in; the caller's
    // request fails on its own and the next one retries the refresh.
    if (isAuthFailure(error)) {
      localStorage.removeItem("user");
      window.location.href = "/";
    }
    return null;
  }
};

// ONE refresh at a time. The gateway's refresh cookie is single-use and rotates on
// every successful POST /refresh-token, so two refreshes in flight together race:
// the first wins and rotates the cookie, the second answers 401 "refresh token not
// match" and used to log the user out. That happened on every access-token expiry
// (900 s) because the 20-second keep-online and check-urgent-task pollers hit 401
// in the same tick and the old interceptor refreshed TWICE per 401. Every caller —
// the interceptors below, checkRefreshTokenWithExpire, and the Bolon client — now
// shares the in-flight promise.
let refreshInFlight: Promise<string | null> | null = null;

export const refreshAccessToken = (): Promise<string | null> => {
  if (!refreshInFlight) {
    refreshInFlight = performRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

const isAccessTokenExpired = (): boolean => {
  const user = getUser()?.state?.user;
  return Boolean(user?.access_token) && Date.now() > Number(user?.expire_at);
};

export const checkRefreshTokenWithExpire = async () => {
  if (isAccessTokenExpired()) {
    await refreshAccessToken();
  }
};

// Create an instance of axios with default settings
const axiosInstance = axios.create({
  baseURL: API_ENDPOINT, // Set your base URL here
  // timeout: 10000, // Set a timeout for requests
  headers: defaultHeaders,
  paramsSerializer: {
    indexes: null, // serialize arrays as ?key=val1&key=val2 (no brackets)
  },
});

// Request interceptor to add access token to headers
axiosInstance.interceptors.request.use(
  async (config: AxiosRequestConfig): Promise<any> => {
    // Refresh BEFORE sending once the stored token is past its expiry (expire_at is
    // exp minus 15 s), so an expired token is normally never sent and the 401 path
    // below is only the fallback for a token revoked server-side.
    if (isAccessTokenExpired()) {
      await refreshAccessToken();
    }
    const token = getAccessToken();
    if (token && config.headers) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle token refresh
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & {
      _retry?: boolean;
    };

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      // If another request already refreshed while this one was in flight, the
      // token in storage differs from the one this request sent: just retry with
      // it. Otherwise refresh (deduped) and retry. The request interceptor puts
      // the current token on the retried request.
      const sentAuthorization = String(
        (originalRequest.headers as Record<string, unknown> | undefined)?.[
          "Authorization"
        ] ?? "",
      );
      const sentToken = sentAuthorization.replace(/^Bearer\s+/, "");
      const currentToken = getAccessToken();
      const token =
        currentToken && currentToken !== sentToken
          ? currentToken
          : await refreshAccessToken();
      if (token) {
        return axiosInstance(originalRequest);
      }
    }

    return Promise.reject(error);
  },
);

// Function to handle GET requests
export const authFetcher = async <T>(
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
  url: string,
  data: any = null,
  params: Record<string, any> | null = null,
  customHeaders: Record<string, any> | null = null,
  withCredentials: boolean = false,
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void,
  signal?: any,
  responseType?: "json" | "blob",
): Promise<T | any> => {
  try {
    // const combinedHeaders = {
    //   ...axiosInstance.defaults.headers.common,
    //   ...defaultHeaders,
    //   ...customHeaders,
    // };

    let combinedHeaders = {};
    if (customHeaders) {
      combinedHeaders = {
        ...axiosInstance.defaults.headers.common,
        ...customHeaders,
      };
    } else {
      combinedHeaders = {
        ...axiosInstance.defaults.headers.common,
        ...defaultHeaders,
      };
    }

    const config: AxiosRequestConfig = {
      method,
      url,
      params,
      headers: combinedHeaders,
      onUploadProgress,
      signal,
      responseType,
    };

    if (withCredentials) {
      config.withCredentials = withCredentials;
    }

    if (data) {
      config.data = data;
    }

    const response: AxiosResponse<T> = await axiosInstance.request(config);
    return responseType == "blob" ? response : response.data;
  } catch (error) {
    console.error("Request error:", error);
    throw error;
  }
};
